import React, { useEffect, useMemo, useState } from 'react';
import AdminProductsRepository from '../../services/AdminProductsRepository.js';
import AdminRepository from '../../services/AdminRepository.js';
import {
  resolveProductLicenseKeys,
  MAX_LICENSE_KEYS_PER_PRODUCT,
  buildSelectableRegistryKeys,
  buildSelectableKeysForProductSlot,
} from '../../utils/licenseKeyUtils.js';
import {
  RegisteredLicenseKeySelectRow,
  EmptyLicenseSlotSelectRow,
  LicenseKeyDeleteModal,
} from './LicenseKeyEditor.jsx';
import './LicenseKeyEditor.css';

/**
 * 製品編集 — ライセンスキー（最大3・ライセンス管理登録済みのみ選択）
 */
const ProductLicenseKeyEditor = ({ productId, product, onUpdated }) => {
  const productsRepo = AdminProductsRepository.getInstance();
  const adminRepo = AdminRepository.getInstance();
  const [busy, setBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteError, setDeleteError] = useState('');
  const [actionError, setActionError] = useState('');
  const [registeredKeys, setRegisteredKeys] = useState([]);
  const [registryLoading, setRegistryLoading] = useState(true);

  const keys = resolveProductLicenseKeys(product);

  useEffect(() => {
    let cancelled = false;
    setRegistryLoading(true);
    adminRepo.getRegisteredLicenseKeys()
      .then((list) => {
        if (!cancelled) setRegisteredKeys(list);
      })
      .catch(() => {
        if (!cancelled) setRegisteredKeys([]);
      })
      .finally(() => {
        if (!cancelled) setRegistryLoading(false);
      });
    return () => { cancelled = true; };
  }, [productId]);

  const selectableForAdd = useMemo(
    () => buildSelectableRegistryKeys(registeredKeys, keys),
    [registeredKeys, keys]
  );

  const runAction = async (fn) => {
    setBusy(true);
    setActionError('');
    try {
      const result = await fn();
      if (onUpdated) onUpdated(result);
    } catch (err) {
      setActionError(err.message || '操作に失敗しました');
      throw err;
    } finally {
      setBusy(false);
    }
  };

  const handleAdd = async (key) => {
    await runAction(() => productsRepo.addProductLicenseKey(productId, key));
  };

  const handleSwap = async (oldKey, newKey) => {
    await runAction(() => productsRepo.swapProductLicenseKey(productId, oldKey, newKey));
  };

  const handleReorder = async (ordered) => {
    await runAction(() => productsRepo.reorderProductLicenseKeys(productId, ordered));
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setBusy(true);
    setDeleteError('');
    try {
      const result = await productsRepo.removeProductLicenseKey(productId, deleteTarget, false);
      setDeleteTarget(null);
      if (onUpdated) onUpdated(result);
    } catch (err) {
      setDeleteError(err.message || '解除に失敗しました');
    } finally {
      setBusy(false);
    }
  };

  const moveKey = async (key, direction) => {
    const idx = keys.indexOf(key);
    if (idx < 0) return;
    const target = direction === 'up' ? idx - 1 : idx + 1;
    if (target < 0 || target >= keys.length) return;
    const next = [...keys];
    next[idx] = keys[target];
    next[target] = keys[idx];
    await handleReorder(next);
  };

  const emptySlotCount = MAX_LICENSE_KEYS_PER_PRODUCT - keys.length;
  const editorDisabled = busy || registryLoading;

  return (
    <div className="license-key-editor">
      {actionError && <p className="license-key-action-error">{actionError}</p>}

      <div className="license-key-list">
        {keys.map((key, index) => (
          <RegisteredLicenseKeySelectRow
            key={`${index}-${key}`}
            slotLabel={`キー${index + 1}`}
            value={key}
            selectOptions={buildSelectableKeysForProductSlot(registeredKeys, keys, key)}
            isPrimary={index === 0}
            canMoveUp={index > 0}
            canMoveDown={index < keys.length - 1}
            onSwap={handleSwap}
            onDelete={(k) => { setDeleteError(''); setDeleteTarget(k); }}
            onMoveUp={() => moveKey(key, 'up')}
            onMoveDown={() => moveKey(key, 'down')}
            disabled={editorDisabled}
          />
        ))}

        {Array.from({ length: emptySlotCount }).map((_, i) => (
          <EmptyLicenseSlotSelectRow
            key={`empty-${i}`}
            slotLabel="追加"
            selectOptions={selectableForAdd}
            onAdd={handleAdd}
            disabled={editorDisabled}
            emptyHint="ライセンス管理で登録済みの未使用キーがありません"
          />
        ))}
      </div>

      <LicenseKeyDeleteModal
        open={!!deleteTarget}
        variant="product"
        keyName={deleteTarget}
        onCancel={() => { setDeleteTarget(null); setDeleteError(''); }}
        onConfirm={handleDeleteConfirm}
        loading={busy}
        error={deleteError}
      />
    </div>
  );
};

export default ProductLicenseKeyEditor;
